import type { FunctionReturnType } from "convex/server";
import { api } from "@convex/_generated/api";
import type { DeliveryLocation } from "./delivery-location";
import { isValidUsZip } from "./delivery-location";

export type SavedShippingAddress = FunctionReturnType<
  typeof api.addresses.queries.listMine
>[number];

export interface SavedAddressOption {
  id: string;
  label: string;
  isDefault?: boolean;
  location: DeliveryLocation;
}

const US_ZIP_PREFIX = /^(\d{5})(?:-\d{4})?$/;

function usZipOf(address: SavedShippingAddress): string | null {
  const zip = address.postalCode?.trim().match(US_ZIP_PREFIX)?.[1];
  return zip && isValidUsZip(zip) ? zip : null;
}

function toSavedAddressOption(
  address: SavedShippingAddress,
  postalCode: string,
): SavedAddressOption {
  return {
    id: address._id,
    isDefault: address.isDefault ?? false,
    label:
      address.label ||
      [
        address.line1,
        address.city,
        `${address.state ?? ""} ${postalCode}`.trim(),
      ]
        .filter(Boolean)
        .join(", "),
    location: {
      zip: postalCode,
      city: address.city ?? "",
      state: address.state ?? "",
      line1: address.line1 ?? undefined,
      line2: address.line2 ?? undefined,
      latitude: address.latitude ?? undefined,
      longitude: address.longitude ?? undefined,
      savedAddressId: address._id,
    },
  };
}

export function savedAddressOptionsFrom(
  addresses: SavedShippingAddress[] | undefined,
): SavedAddressOption[] {
  return (addresses ?? []).flatMap((address) => {
    const zip = usZipOf(address);
    return zip ? [toSavedAddressOption(address, zip)] : [];
  });
}
